import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLinkClick = () => setIsOpen(false);

  const navLinks = [
    { name: 'Home', path: '/', id: 'nav-home' },
    { name: 'About', path: '/about', id: 'nav-about' },
    { name: 'Experience', path: '/#experience', id: 'nav-exp' },
    { name: 'Projects', path: '/#projects', id: 'nav-projects' },
    { name: 'Certs', path: '/certifications', id: 'nav-certs' },
    { name: 'Contact', path: '/contact', id: 'nav-contact' },
  ];

  const handleNavClick = (path: string) => {
    handleLinkClick();

    if (path === '/certifications' || path === '/about' || path === '/contact') {
      window.history.pushState({}, '', path);
      window.dispatchEvent(new Event('popstate'));
      window.scrollTo(0, 0);
      return;
    }

    if (path === '/') {
      if (window.location.pathname !== '/') {
        window.history.pushState({}, '', '/');
        window.dispatchEvent(new Event('popstate'));
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (path.startsWith('/#')) {
      const id = path.replace('/#', '');
      if (window.location.pathname !== '/') {
        window.history.pushState({}, '', '/');
        window.dispatchEvent(new Event('popstate'));
        setTimeout(() => {
          const el = document.getElementById(id);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };



  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-[80] transition-all duration-500 ${isScrolled || isOpen ? 'glass-nav py-3' : 'bg-transparent py-6'
          }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">

          {/* Logo */}
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); handleNavClick('/'); }}
            className="flex items-center gap-3 group"
            id="nav-logo"
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm mono transition-all duration-300"
              style={{
                background: 'rgba(0,255,136,0.1)',
                border: '1px solid rgba(0,255,136,0.4)',
                color: 'var(--neon-green)',
                boxShadow: '0 0 15px rgba(0,255,136,0.2)',
              }}
            >
              D
            </div>
            <div className="flex flex-col">
              <span
                className="text-lg font-black tracking-tighter leading-none"
                style={{ color: 'var(--text-primary)' }}
              >
                DarshanXtobi
              </span>
              <span className="mono text-[9px] tracking-widest flex items-center gap-1.5" style={{ color: 'var(--neon-green)' }}>
                <span className="status-dot" style={{ width: '5px', height: '5px' }} />
                CyberSec &amp; Dev
              </span>
            </div>
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map(link => (
              <button
                key={link.name}
                id={link.id}
                onClick={() => handleNavClick(link.path)}
                className="group relative mono text-xs font-bold uppercase tracking-widest transition-colors duration-300 bg-transparent border-none cursor-pointer"
                style={{ color: 'var(--text-muted)' }}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = 'var(--neon-green)'}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)'}
              >
                {link.name}
                <span
                  className="absolute bottom-0 left-0 w-0 h-px transition-all duration-300 ease-out group-hover:w-full"
                  style={{ background: 'var(--neon-green)', boxShadow: '0 0 6px var(--neon-green)' }}
                />
              </button>
            ))}
          </div>

          {/* CTA + Hamburger */}
          <div className="flex items-center gap-4">
            <a
              id="navbar-resume-download"
              href={`${import.meta.env.BASE_URL || '/'}Darshan-Resume.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-2 mono text-xs font-bold uppercase tracking-wider px-5 py-3 rounded-lg transition-all duration-300"
              style={{
                background: 'rgba(0,255,136,0.08)',
                border: '1px solid rgba(0,255,136,0.3)',
                color: 'var(--neon-green)',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(0,255,136,0.15)';
                (e.currentTarget as HTMLElement).style.boxShadow = 'var(--glow-green)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(0,255,136,0.08)';
                (e.currentTarget as HTMLElement).style.boxShadow = 'none';
              }}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Resume
            </a>

            {/* Hamburger */}
            <button
              type="button"
              id="navbar-hamburger"
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 relative focus:outline-none"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
            >
              {[0, 1, 2].map(i => (
                <motion.span
                  key={i}
                  animate={
                    isOpen
                      ? i === 0 ? { rotate: 45, y: 6 }
                        : i === 1 ? { opacity: 0, x: -10 }
                          : { rotate: -45, y: -6 }
                      : { rotate: 0, y: 0, opacity: 1, x: 0 }
                  }
                  className="block h-0.5 rounded-full"
                  style={{
                    width: i === 1 ? '16px' : '20px',
                    background: 'var(--neon-green)',
                    boxShadow: '0 0 5px var(--neon-green)',
                  }}
                />
              ))}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[70] lg:hidden overflow-y-auto"
            style={{ background: 'var(--bg-primary)' }}
          >
            <div className="pt-28 pb-12 px-8 flex flex-col min-h-screen">

              {/* Cyber background effect */}
              <div className="absolute inset-0 cyber-grid-bg opacity-30 pointer-events-none" />

              <div className="relative z-10 flex-1 space-y-2">
                <p className="mono text-xs tracking-widest mb-8" style={{ color: 'var(--neon-green)' }}>
                  // navigation
                </p>
                {navLinks.map((link, idx) => (
                  <motion.button
                    key={link.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + idx * 0.06 }}
                    onClick={() => handleNavClick(link.path)}
                    className="w-full flex items-center justify-between py-4 bg-transparent border-none cursor-pointer"
                    style={{ borderBottom: '1px solid var(--border-subtle)' }}
                  >
                    <div className="flex items-center gap-4">
                      <span className="mono text-xs font-bold" style={{ color: 'var(--text-muted)' }}>0{idx + 1}</span>
                      <span className="text-3xl font-black tracking-tighter uppercase" style={{ color: 'var(--text-primary)' }}>
                        {link.name}
                      </span>
                    </div>
                    <span style={{ color: 'var(--neon-green)' }}>→</span>
                  </motion.button>
                ))}
              </div>

              <div className="relative z-10 mt-12 space-y-6">
                <a
                  href={`${import.meta.env.BASE_URL || '/'}Darshan-Resume.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleLinkClick}
                  className="w-full py-5 rounded-2xl flex items-center justify-center gap-3 mono text-sm font-bold uppercase tracking-widest transition-all"
                  style={{
                    background: 'rgba(0,255,136,0.1)',
                    border: '1px solid rgba(0,255,136,0.3)',
                    color: 'var(--neon-green)',
                  }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download Resume
                </a>

                <p className="mono text-xs text-center" style={{ color: 'var(--text-muted)' }}>
                  © {new Date().getFullYear()} Darshan
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
